import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { Calendar, Clock, User, FileText, CheckCircle, XCircle, AlertCircle, ClipboardList } from 'lucide-react';
import './LeaveApprovalView.css';

const LeaveApprovalView = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('pending'); // all, pending
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    managerApproved: 0,
    adminApproved: 0,
    rejected: 0
  });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'approve' or 'reject'
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [comment, setComment] = useState('');

  useEffect(() => {
    fetchLeaveRequests();
  }, [filter]);

  const fetchLeaveRequests = async () => {
    try {
      setLoading(true);
      // Fetch all requests
      const response = await leaveAPI.getAllRequests();
      const allRequests = response.data.leaveRequests || [];
      
      // Calculate statistics
      const newStats = {
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'PENDING').length,
        managerApproved: allRequests.filter(r => r.status === 'MANAGER_APPROVED').length,
        adminApproved: allRequests.filter(r => r.status === 'ADMIN_APPROVED').length,
        rejected: allRequests.filter(r => r.status === 'REJECTED').length
      };
      setStats(newStats);
      
      // Filter based on selected filter
      let filteredRequests = allRequests;
      if (filter === 'pending') {
        filteredRequests = allRequests.filter(r => r.status === 'PENDING' || r.status === 'MANAGER_APPROVED');
      }
      
      setLeaveRequests(filteredRequests);
    } catch (error) {
      toast.error('Failed to fetch leave requests');
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setSelectedRequestId(id);
    setModalType('approve');
    setComment('');
    setShowModal(true);
  };

  const handleReject = async (id) => {
    setSelectedRequestId(id);
    setModalType('reject');
    setComment('');
    setShowModal(true);
  };

  const handleModalSubmit = async () => {
    if (modalType === 'reject' && !comment.trim()) {
      toast.warning('Rejection reason is required');
      return;
    }
    
    try {
      setActionLoading(selectedRequestId);
      
      if (modalType === 'approve') {
        await leaveAPI.adminApprove(selectedRequestId, comment || '');
        toast.success('Leave request approved successfully');
      } else {
        await leaveAPI.adminReject(selectedRequestId, comment);
        toast.success('Leave request rejected');
      }
      
      // Close modal and reset all state first
      setShowModal(false);
      setComment('');
      setSelectedRequestId(null);
      setModalType('');
      setActionLoading(null);
      
      // Small delay to ensure backend has processed, then fetch updated data
      setTimeout(() => {
        fetchLeaveRequests();
      }, 300);
      
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${modalType} request`);
      setActionLoading(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setComment('');
    setSelectedRequestId(null);
    setModalType('');
  };

  return (
    <div className="leave-approval-container">
      <div className="leave-approval-header">
        <div className="header-title">
          <ClipboardList size={28} />
          <h2>LEAVE REQUEST APPROVALS (ADMIN)</h2>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.managerApproved}</div>
          <div className="stat-label">Manager Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.adminApproved}</div>
          <div className="stat-label">Admin Approved</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={filter === 'pending' ? 'tab-button' : 'tab-button inactive'}
          onClick={() => setFilter('pending')}
        >
          ⏳ Awaiting Admin Action
        </button>
        <button
          className={filter === 'all' ? 'tab-button active' : 'tab-button inactive'}
          onClick={() => setFilter('all')}
        >
          📋 All Requests
        </button>
      </div>

      {loading ? (
        <div className="loading-state">Loading leave requests...</div>
      ) : leaveRequests.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>No leave requests found</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="leave-table">
            <thead>
              <tr>
                <th>EMPID</th>
                <th>NAME</th>
                <th>DEPT</th>
                <th>TYPE</th>
                <th>DATES</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {leaveRequests.map((request) => (
                <tr key={request.id}>
                  <td>{request.employee_id }</td>
                  <td>{request.employee_name}</td>
                  <td>{request.department || 'hr'}</td>
                  <td>{request.leave_type}</td>
                  <td>
                    <div className="date-range">
                      {new Date(request.start_date).toLocaleDateString('en-CA')} → {new Date(request.end_date).toLocaleDateString('en-CA')}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${request.status.toLowerCase()}`}>
                      {request.status === 'PENDING' ? 'PENDING' : 
                       request.status === 'MANAGER_APPROVED' ? 'PENDING' :
                       request.status === 'ADMIN_APPROVED' ? 'APPROVED' : 'REJECTED'}
                    </span>
                  </td>
                  <td>
                    {(request.status === 'PENDING' || request.status === 'MANAGER_APPROVED') ? (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleApprove(request.id)}
                          disabled={actionLoading === request.id}
                          className="action-btn approve-btn"
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <CheckCircle size={14} />
                          Approve
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          disabled={actionLoading === request.id}
                          className="action-btn reject-btn"
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <XCircle size={14} />
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>No action needed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Custom Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{modalType === 'approve' ? 'APPROVE LEAVE REQUEST' : 'REJECT LEAVE REQUEST'}</h3>
            <p className="modal-label">
              {modalType === 'approve' ? 'Enter approval comment (optional):' : 'Enter rejection reason:'}
            </p>
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={modalType === 'approve' ? 'Optional comment...' : 'Rejection reason...'}
              className="modal-input"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleModalSubmit();
                }
              }}
            />
            <div className="modal-buttons">
              <button className="modal-btn-ok" onClick={handleModalSubmit} disabled={actionLoading}>
                OK
              </button>
              <button className="modal-btn-cancel" onClick={handleModalClose} disabled={actionLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveApprovalView;
